import * as React from 'react';
import { cn } from '../lib/utils';

/**
 * Layout uses div + CSS Grid (not <table>) for column sizing control.
 * Every TableRow reads --col-template via grid-template-columns.
 *
 * Set column widths via the `columns` prop:
 *   <Table columns={5}>              → repeat(5, minmax(0, 1fr))
 *   <Table columns="200px 1fr 1fr">  → explicit grid template
 *
 * Alternatively, pass --col-template through `style` for advanced cases
 * (e.g. when the solink-ui DataTable computes sizes dynamically).
 */

interface TableProps extends React.ComponentProps<'div'> {
  /** Column count (equal-width) or explicit grid-template-columns string. */
  columns?: number | string;
}

interface TableHeadCellProps extends React.ComponentProps<'div'> {
  colSpan?: number;
}

function Table({ className, columns, style, ...props }: TableProps) {
  const colTemplate = typeof columns === 'number' ? `repeat(${columns}, minmax(0, 1fr))` : columns;

  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <div
        data-slot="table"
        role="table"
        className={cn('w-full caption-bottom text-sm', className)}
        style={
          {
            ...(colTemplate ? { '--col-template': colTemplate } : undefined),
            ...style,
          } as React.CSSProperties
        }
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div role="rowgroup" data-slot="table-header" className={className} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role="rowgroup"
      data-slot="table-body"
      className={cn(
        '[&>[data-slot=table-row]:last-child>[data-slot=table-cell]]:border-b-0',
        className,
      )}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role="rowgroup"
      data-slot="table-footer"
      className={cn(
        'border-t font-medium bg-muted/50 [&>[data-slot=table-row]:last-child>[data-slot=table-cell]]:border-b-0',
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, style, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role="row"
      data-slot="table-row"
      className={cn('group grid items-center', className)}
      style={{
        gridTemplateColumns: 'var(--col-template)',
        ...style,
      }}
      {...props}
    />
  );
}

function TableHead({ className, colSpan: colSpanProp, style, ...props }: TableHeadCellProps) {
  return (
    <div
      role="columnheader"
      data-slot="table-head"
      className={cn(
        'flex h-10 min-w-0 items-center overflow-hidden whitespace-nowrap border-b px-4 py-3 text-left font-medium text-foreground-muted label-md [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      style={{
        ...(colSpanProp && colSpanProp > 1 ? { gridColumn: `span ${colSpanProp}` } : undefined),
        ...style,
      }}
      {...props}
    />
  );
}

function TableCell({ className, colSpan: colSpanProp, style, ...props }: TableHeadCellProps) {
  return (
    <div
      role="cell"
      data-slot="table-cell"
      className={cn(
        'flex h-14 min-w-0 items-center overflow-hidden whitespace-nowrap border-b p-2 px-4 transition-colors body-md group-hover:bg-muted/50 group-data-[state=selected]:bg-muted [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      style={{
        ...(colSpanProp && colSpanProp > 1 ? { gridColumn: `span ${colSpanProp}` } : undefined),
        ...style,
      }}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="table-caption"
      className={cn('mt-4 text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
